import * as React from 'react'
import { connect } from 'react-redux'
import {
  suggestArticles,
  searchArticles,
  navigateToArticle,
  navigateToSearchResults,
  clearSuggestions,
  updateSearchInput,
  clickedSuggestArticle,
} from '../../actions/answers'
import { strings } from '../../lib/localization'
import debounce from 'lodash/debounce'
import {
  popularQuestionsSelector,
  popularQuestionsStatusSelector,
} from '../../selectors/popularquestions'
import {
  currentSearchResultSelector,
  currentSearchStatusSelector,
  currentSearchStringSelector,
} from '../../selectors/search'
import {
  currentSuggestResultsSelector,
  currentSuggestStatusSelector,
} from '../../selectors/suggest'
import { currentParamsSelector } from '../../selectors/navigation'
import { SearchItem } from './searchitem'
import { SuggestItem } from './suggestitem'
import {
  userIdFromAnswersHubSelector,
  formIdFromAnswersHubSelector,
} from '../../selectors/hub'
import { RootState } from '../../types/rootstate'

import '../../sass/answers/searchfield.scss'

interface StateProps {
  suggestStatus: string
  suggestions: RootState['suggest']['key']['suggestions']
  searchStatus: string
  searchResult: RootState['search']['key']['result']
  searchString: string
  userId: number
  answersId: number
}

interface DispatchProps {
  searchArticles: (userId: number, answersId: number, search: string) => void
  suggestArticles: (userId: number, answersId: number, search: string) => void
  clearSuggestions: (answersId: number) => void
  navigateToArticle: (
    userId: number,
    answersId: number,
    articleId: number
  ) => void
  navigateToSearchResults: () => void
  updateSearchInput: (answersId: number, search: string) => void
  clickedSuggestArticle: (search: string, articleId: number) => void
}

interface OwnProps {
  updated?: () => void
}

type SearchFieldHeaderProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState): StateProps {
  return {
    suggestStatus: currentSuggestStatusSelector(state),
    suggestions: currentSuggestResultsSelector(state),
    searchStatus: currentSearchStatusSelector(state),
    searchResult: currentSearchResultSelector(state),
    searchString: currentSearchStringSelector(state),
    userId: userIdFromAnswersHubSelector(state),
    answersId: formIdFromAnswersHubSelector(state),
  }
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    searchArticles: (userId, answersId, search) =>
      dispatch(searchArticles(userId, answersId, search)),
    suggestArticles: (userId, answersId, search) =>
      dispatch(suggestArticles(userId, answersId, search)),
    clearSuggestions: (answersId) => dispatch(clearSuggestions(answersId)),
    navigateToArticle: (userId, answersId, articleId) =>
      dispatch(navigateToArticle(userId, answersId, articleId)),
    navigateToSearchResults: () => dispatch(navigateToSearchResults()),
    updateSearchInput: (answersId, search) =>
      dispatch(updateSearchInput(answersId, search)),
    clickedSuggestArticle: (search, articleId) =>
      dispatch(clickedSuggestArticle(search, articleId)),
  }
}

interface SearchFieldHeaderState {
  searchFieldSelectionStart: number
  searchFieldSelectionEnd: number
}

class SearchFieldHeaderComponent extends React.Component<
  SearchFieldHeaderProps,
  SearchFieldHeaderState
> {
  references: {
    searchInput: HTMLInputElement
  } = {
    searchInput: null,
  }

  constructor(props) {
    super(props)

    this.state = {
      searchFieldSelectionStart: 0,
      searchFieldSelectionEnd: 0,
    }
  }

  componentDidUpdate() {
    this.props.updated && this.props.updated()
    this.references.searchInput.selectionStart =
      this.state.searchFieldSelectionStart
    this.references.searchInput.selectionEnd =
      this.state.searchFieldSelectionEnd
  }

  debouncedSuggestArticles = debounce(() => {
    if (this.props.searchString !== '') {
      this.props.suggestArticles(
        this.props.userId,
        this.props.answersId,
        this.props.searchString
      )
    } else {
      this.props.clearSuggestions(this.props.answersId)
    }
  }, 250)

  handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      this.doSearch()
    }
  }

  doSearch = () => {
    if (this.props.searchString.trim().length > 0) {
      this.references.searchInput.blur()

      this.props.searchArticles(
        this.props.userId,
        this.props.answersId,
        this.props.searchString
      )
      this.props.navigateToSearchResults()
    }
  }

  handleSearch = (e) => {
    this.doSearch()
  }

  handleSearchKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.doSearch()
    }
  }

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.updateSearchInput(this.props.answersId, e.target.value)
    this.debouncedSuggestArticles()
    this.setState({
      searchFieldSelectionStart: e.target.selectionStart,
      searchFieldSelectionEnd: e.target.selectionEnd,
    })
  }

  handleSuggestItemClick = (articleId: number) => {
    this.props.clickedSuggestArticle(this.props.searchString, articleId)
    this.props.navigateToArticle(
      this.props.userId,
      this.props.answersId,
      articleId
    )
  }

  render() {
    const hasResult = this.props.suggestions.length > 0

    return (
      <div className="answers-search">
        {this.props.searchStatus === 'fetching' && (
          <div className="spinner">
            <div className="bounce1 accentPrimary" />
            <div className="bounce2 accentPrimary" />
            <div className="bounce3 accentPrimary" />
          </div>
        )}

        <input
          ref={(node) => (this.references.searchInput = node)}
          type="text"
          value={this.props.searchString}
          onChange={this.handleSearchChange}
          onKeyPress={this.handleSearchKeyPress}
          placeholder={strings.answersSearchPlaceholder}
        />

        <div className="clear-search">
          {this.props.searchStatus !== 'fetching' && (
            <i
              onClick={this.handleSearch}
              className="material-icons"
              role="button"
              tabIndex={0}
              onKeyDown={this.handleSearchKeyDown}
            >
              search
            </i>
          )}
        </div>

        <div
          className={'answers-search-suggest' + (hasResult ? ' active' : '')}
        >
          {this.props.suggestions.map((suggestion) => (
            <SuggestItem
              key={suggestion.articleId}
              item={suggestion}
              onClick={this.handleSuggestItemClick}
            />
          ))}
        </div>
      </div>
    )
  }
}

const SearchFieldHeader = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(SearchFieldHeaderComponent)

export { SearchFieldHeader }
